import * as _ from 'lodash';

import { CuttingCustomHole, FastenerBasePointEnum } from '../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../models/cutting/cutting-fixed-fasteners-set.model';
import { GrooveTypeEnum } from '../models/cutting/cutting-panel-base-slot.model';
import {
  AngleMode,
  AnglePosition,
  Cutout,
  CutoutPoint,
  CutoutTypeEnum,
  MasonsMitreFemaleParams,
  MasonsMitreMaleParams,
  MasonsMitreOrientationEnum,
  OutlineAngle,
  PlateTypeEnum,
} from '../models/cutting/cutting-panel-outline.model';
import { CuttingPanelSlot } from '../models/cutting/cutting-panel-slot.model';
import {
  AxisTextData,
  CutoutFragmentData,
  CutoutPathData,
  FastenersSetPathData,
  HolePathData,
  LineData,
  PathData,
  Point,
  Position,
  PositionedPoint,
  RectangleData,
  TextData,
} from '../models/cutting/cutting-svg.model';
import { PanelBase } from '../models/cutting/panel-base.model';
import { PostformingSideEnum } from '../models/user/cutting-settings.model';
import { CuttingPanelHelper } from './cutting-panel.helper';
import { FastenersHelper } from './fasteners.helper';

export class SVG {
  element: Element;
  svgNS: string;
  textOffset = 5;
  textSize = 12;
  private vb: number[];
  private h: number;
  private w: number;

  get startDrawPoint(): Point {
    const textFullOfset: number = +this.textOffset + +this.textSize;
    return { x: +textFullOfset, y: +textFullOfset };
  }

  get height(): number {
    return this.h;
  }
  set height(val: number) {
    this.h = val;
    this.element.setAttribute('height', val.toString());
  }

  get width(): number {
    return this.w;
  }
  set width(val: number) {
    this.w = val;
    this.element.setAttribute('width', val.toString());
  }

  get viewBox(): number[] {
    return this.vb;
  }
  set viewBox(val: number[]) {
    this.vb = val;
    this.element.setAttribute('viewBox', val.join(' '));
  }

  constructor(size: number, xScale: number, yScale: number) {
    this.svgNS = 'http://www.w3.org/2000/svg';
    this.element = document.createElementNS(this.svgNS, 'svg');
    this.element.setAttribute('xmlns', this.svgNS);

    const defs = this.getDefs();
    this.element.appendChild(defs);

    this.height = size * yScale;
    this.width = size * xScale;

    // for cuted length
    if (this.height < size) {
      this.height += this.startDrawPoint.y * 2;
    }
    if (this.width < size) {
      this.width += this.startDrawPoint.x * 2;
    }

    this.viewBox = [0, 0, this.width, this.height];
  }

  resize(size: number, xScale: number, yScale: number) {
    if (!this.element) {
      return;
    }

    this.height = size * yScale;
    this.width = size * xScale;

    // for cuted length
    if (this.height < size) {
      this.height += this.startDrawPoint.y * 2;
    }
    if (this.width < size) {
      this.width += this.startDrawPoint.x * 2;
    }

    this.viewBox = [0, 0, this.width, this.height];
  }

  getPostformingLineData(panel: PanelBase, postformingSide: PostformingSideEnum): LineData {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const postformingOffset = 3;

    const lineY =
      postformingSide === PostformingSideEnum.TopAndBottom
        ? this.startDrawPoint.y - postformingOffset
        : this.startDrawPoint.y + postformingOffset + panelWidth;

    const data: LineData = {
      p1: { x: this.startDrawPoint.x, y: lineY },
      p2: { x: this.startDrawPoint.x + panelHeight, y: lineY },
    };

    return data;
  }

  getMainOutlinePathData(panel: PanelBase): PathData[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const topLeftRounding: number = panel.panelOutline.topLeft.rounding * scale;
    const topRightRounding: number = panel.panelOutline.topRight.rounding * scale;
    const bottomLeftRounding: number = panel.panelOutline.bottomLeft.rounding * scale;
    const bottomRightRounding: number = panel.panelOutline.bottomRight.rounding * scale;

    const topLeftRoundingInside: number = panel.panelOutline.topLeft.roundingInside * scale;
    const topRightRoundingInside: number = panel.panelOutline.topRight.roundingInside * scale;
    const bottomLeftRoundingInside: number = panel.panelOutline.bottomLeft.roundingInside * scale;
    const bottomRightRoundingInside: number = panel.panelOutline.bottomRight.roundingInside * scale;

    const topLeftChamfer: number = panel.panelOutline.topLeft.chamferLength * scale;
    const topRightChamfer: number = panel.panelOutline.topRight.chamferLength * scale;
    const bottomRightChamfer: number = panel.panelOutline.bottomRight.chamferLength * scale;
    const bottomLeftChamfer: number = panel.panelOutline.bottomLeft.chamferLength * scale;

    const topLeftCutInsideRadius = +panel.panelOutline.topLeft.cutInsideR * scale;
    const topRightCutInsideRadius = +panel.panelOutline.topRight.cutInsideR * scale;
    const bottomLeftCutInsideRadius = +panel.panelOutline.bottomLeft.cutInsideR * scale;
    const bottomRightCutInsideRadius = +panel.panelOutline.bottomRight.cutInsideR * scale;

    const topLeftCutInsideWithAngleRadius = +panel.panelOutline.topLeft.cutInsideWithAngle.r * scale;
    const topRightCutInsideWithAngleRadius = +panel.panelOutline.topRight.cutInsideWithAngle.r * scale;
    const bottomLeftCutInsideWithAngleRadius = +panel.panelOutline.bottomLeft.cutInsideWithAngle.r * scale;
    const bottomRightCutInsideWithAngleRadius = +panel.panelOutline.bottomRight.cutInsideWithAngle.r * scale;

    const topLeftMasonsMitreFemaleParams: MasonsMitreFemaleParams = {
      length: +panel.panelOutline.topLeft.masonsMitreFemale.length * scale,
      allowance: +panel.panelOutline.topLeft.masonsMitreFemale.allowance * scale,
      orientation: panel.panelOutline.topLeft.masonsMitreFemale.orientation,
      radius: +panel.panelOutline.topLeft.masonsMitreFemale.radius * scale,
    };
    const topLeftMasonsMitreMaleParams: MasonsMitreMaleParams = {
      allowance: +panel.panelOutline.topLeft.masonsMitreMale.allowance * scale,
      orientation: panel.panelOutline.topLeft.masonsMitreMale.orientation,
      radius: +panel.panelOutline.topLeft.masonsMitreMale.radius * scale,
    };
    const topRightMasonsMitreFemaleParams: MasonsMitreFemaleParams = {
      length: +panel.panelOutline.topRight.masonsMitreFemale.length * scale,
      allowance: +panel.panelOutline.topRight.masonsMitreFemale.allowance * scale,
      orientation: panel.panelOutline.topRight.masonsMitreFemale.orientation,
      radius: +panel.panelOutline.topRight.masonsMitreFemale.radius * scale,
    };
    const topRightMasonsMitreMaleParams: MasonsMitreMaleParams = {
      allowance: +panel.panelOutline.topRight.masonsMitreMale.allowance * scale,
      orientation: panel.panelOutline.topRight.masonsMitreMale.orientation,
      radius: +panel.panelOutline.topRight.masonsMitreMale.radius * scale,
    };
    const bottomRightMasonsMitreFemaleParams: MasonsMitreFemaleParams = {
      length: +panel.panelOutline.bottomRight.masonsMitreFemale.length * scale,
      allowance: +panel.panelOutline.bottomRight.masonsMitreFemale.allowance * scale,
      orientation: panel.panelOutline.bottomRight.masonsMitreFemale.orientation,
      radius: +panel.panelOutline.bottomRight.masonsMitreFemale.radius * scale,
    };
    const bottomRightMasonsMitreMaleParams: MasonsMitreMaleParams = {
      allowance: +panel.panelOutline.bottomRight.masonsMitreMale.allowance * scale,
      orientation: panel.panelOutline.bottomRight.masonsMitreMale.orientation,
      radius: +panel.panelOutline.bottomRight.masonsMitreMale.radius * scale,
    };
    const bottomLeftMasonsMitreFemaleParams: MasonsMitreFemaleParams = {
      length: +panel.panelOutline.bottomLeft.masonsMitreFemale.length * scale,
      allowance: +panel.panelOutline.bottomLeft.masonsMitreFemale.allowance * scale,
      orientation: panel.panelOutline.bottomLeft.masonsMitreFemale.orientation,
      radius: +panel.panelOutline.bottomLeft.masonsMitreFemale.radius * scale,
    };
    const bottomLeftMasonsMitreMaleParams: MasonsMitreMaleParams = {
      allowance: +panel.panelOutline.bottomLeft.masonsMitreMale.allowance * scale,
      orientation: panel.panelOutline.bottomLeft.masonsMitreMale.orientation,
      radius: +panel.panelOutline.bottomLeft.masonsMitreMale.radius * scale,
    };

    const topLeftCutLength: Point = {
      x: panel.panelOutline.topLeft.cutX * scale,
      y: panel.panelOutline.topLeft.cutY * scale,
    };
    const topRightCutLength: Point = {
      x: panel.panelOutline.topRight.cutX * scale,
      y: panel.panelOutline.topRight.cutY * scale,
    };
    const bottomLeftCutLength: Point = {
      x: panel.panelOutline.bottomLeft.cutX * scale,
      y: panel.panelOutline.bottomLeft.cutY * scale,
    };
    const bottomRightCutLength: Point = {
      x: panel.panelOutline.bottomRight.cutX * scale,
      y: panel.panelOutline.bottomRight.cutY * scale,
    };
    const topLeftCutInsideLength: Point = {
      x: panel.panelOutline.topLeft.cutInsideX * scale,
      y: panel.panelOutline.topLeft.cutInsideY * scale,
    };
    const topRightCutInsideLength: Point = {
      x: panel.panelOutline.topRight.cutInsideX * scale,
      y: panel.panelOutline.topRight.cutInsideY * scale,
    };
    const bottomLeftCutInsideLength: Point = {
      x: panel.panelOutline.bottomLeft.cutInsideX * scale,
      y: panel.panelOutline.bottomLeft.cutInsideY * scale,
    };
    const bottomRightCutInsideLength: Point = {
      x: panel.panelOutline.bottomRight.cutInsideX * scale,
      y: panel.panelOutline.bottomRight.cutInsideY * scale,
    };

    const topLeftCutInsideWithAngleLength: Point = {
      x: panel.panelOutline.topLeft.cutInsideWithAngle.x * scale,
      y: panel.panelOutline.topLeft.cutInsideWithAngle.y * scale,
    };
    const topRightCutInsideWithAngleLength: Point = {
      x: panel.panelOutline.topRight.cutInsideWithAngle.x * scale,
      y: panel.panelOutline.topRight.cutInsideWithAngle.y * scale,
    };
    const bottomLeftCutInsideWithAngleLength: Point = {
      x: panel.panelOutline.bottomLeft.cutInsideWithAngle.x * scale,
      y: panel.panelOutline.bottomLeft.cutInsideWithAngle.y * scale,
    };
    const bottomRightCutInsideWithAngleLength: Point = {
      x: panel.panelOutline.bottomRight.cutInsideWithAngle.x * scale,
      y: panel.panelOutline.bottomRight.cutInsideWithAngle.y * scale,
    };

    const topLeftCutInsideWithAngleAnglePoint: Point = {
      x: panel.panelOutline.topLeft.cutInsideWithAngle.x1 * scale,
      y: panel.panelOutline.topLeft.cutInsideWithAngle.y1 * scale,
    };

    const topLeftCutInsideWithAngleAnglePointRadius = panel.panelOutline.topLeft.cutInsideWithAngle.r * scale;

    const topRightCutInsideWithAngleAnglePoint = this.findIntersectionPoint(
      90 - panel.panelOutline.topRight.cutInsideWithAngle.yA,
      0,
      -topRightCutInsideWithAngleLength.y,
      panel.panelOutline.topRight.cutInsideWithAngle.xA,
      -topRightCutInsideWithAngleLength.x,
      0
    );
    const bottomRightCutInsideWithAngleAnglePoint = this.findIntersectionPoint(
      90 + panel.panelOutline.bottomRight.cutInsideWithAngle.yA,
      0,
      bottomRightCutInsideWithAngleLength.y,
      -panel.panelOutline.bottomRight.cutInsideWithAngle.xA,
      -bottomRightCutInsideWithAngleLength.x,
      0
    );
    const bottomLeftCutInsideWithAngleAnglePoint = this.findIntersectionPoint(
      90 - panel.panelOutline.bottomLeft.cutInsideWithAngle.yA,
      0,
      bottomLeftCutInsideWithAngleLength.y,
      panel.panelOutline.bottomLeft.cutInsideWithAngle.xA,
      bottomLeftCutInsideWithAngleLength.x,
      0
    );

    const getCutInsightWithAngleRadiusPoints = (
      cutInsideWithAngleAnglePoint: {
        point: Point;
        zeroPoint1: Point;
        zeroPoint2: Point;
        angle: number;
      },
      cutInsideWithAngleRadius: number
    ) => ({
      point1: this.pointOnSegment(
        this.getCircleRemotenessFromCornerTop(cutInsideWithAngleAnglePoint.angle, cutInsideWithAngleRadius),
        cutInsideWithAngleAnglePoint.point,
        cutInsideWithAngleAnglePoint.zeroPoint1
      ),
      point2: this.pointOnSegment(
        this.getCircleRemotenessFromCornerTop(cutInsideWithAngleAnglePoint.angle, cutInsideWithAngleRadius),
        cutInsideWithAngleAnglePoint.point,
        cutInsideWithAngleAnglePoint.zeroPoint2
      ),
    });

    // const topLeftCutInsideWithAngleRadiusPoints = getCutInsightWithAngleRadiusPoints(
    //   topLeftCutInsideWithAngleAnglePoint,
    //   topLeftCutInsideWithAngleRadius
    // );
    const topRightCutInsideWithAngleRadiusPoints = getCutInsightWithAngleRadiusPoints(
      topRightCutInsideWithAngleAnglePoint,
      topRightCutInsideWithAngleRadius
    );
    const bottomRightCutInsideWithAngleRadiusPoints = getCutInsightWithAngleRadiusPoints(
      bottomRightCutInsideWithAngleAnglePoint,
      bottomRightCutInsideWithAngleRadius
    );
    const bottomLeftCutInsideWithAngleRadiusPoints = getCutInsightWithAngleRadiusPoints(
      bottomLeftCutInsideWithAngleAnglePoint,
      bottomLeftCutInsideWithAngleRadius
    );

    const data: PathData[] = [];

    /* TOP LEFT ANGLE */
    if (topLeftRounding > 0 && panel.panelOutline.topLeft.mode === AngleMode.Rounded) {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, +this.startDrawPoint.y + +topLeftRounding] });
      data.push({
        mode: 'a',
        data: [topLeftRounding, topLeftRounding, 0, 0, 1, topLeftRounding, -topLeftRounding],
      });
    }
    // cut
    else if (+topLeftCutLength.x * +topLeftCutLength.y > 0 && panel.panelOutline.topLeft.mode === AngleMode.Cutted) {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, +this.startDrawPoint.y + +topLeftCutLength.y] });
      data.push({ mode: 'l', data: [topLeftCutLength.x, -topLeftCutLength.y] });
    }
    // roung inside
    else if (+topLeftRoundingInside > 0 && panel.panelOutline.topLeft.mode === AngleMode.RoundedInside) {
      data.push({ mode: 'M', data: [+this.startDrawPoint.x, +this.startDrawPoint.y + +topLeftRoundingInside] });
      data.push({
        mode: 'a',
        data: [+topLeftRoundingInside, topLeftRoundingInside, 0, 0, 0, topLeftRoundingInside, -topLeftRoundingInside],
      });
    }
    // diagonal cut
    else if (
      panel.panelOutline.topLeft.mode === AngleMode.DiagonalCut &&
      panel.panelOutline.bottomLeft.mode === AngleMode.None &&
      panel.panelOutline.topRight.mode === AngleMode.None &&
      panel.panelOutline.bottomRight.mode !== AngleMode.DiagonalCut
    ) {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, +this.startDrawPoint.y + +panelWidth] });
      data.push({ mode: 'l', data: [+panelHeight, -panelWidth] });
    }
    // inside cut
    else if (
      +topLeftCutInsideLength.x * +topLeftCutInsideLength.y > 0 &&
      Math.min(+topLeftCutInsideLength.x, +topLeftCutInsideLength.y) >= topLeftCutInsideRadius &&
      panel.panelOutline.topLeft.mode === AngleMode.CuttedInside
    ) {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, +this.startDrawPoint.y + +topLeftCutInsideLength.y] });
      data.push({ mode: 'l', data: [topLeftCutInsideLength.x - topLeftCutInsideRadius, 0] });
      data.push({
        mode: 'q',
        data: [+topLeftCutInsideRadius, 0, +topLeftCutInsideRadius, -topLeftCutInsideRadius],
      });
      data.push({ mode: 'l', data: [0, -topLeftCutInsideLength.y + topLeftCutInsideRadius] });
    }
    // chamfer
    else if (topLeftChamfer > 0 && panel.panelOutline.topLeft.mode === AngleMode.Chamfer) {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, +this.startDrawPoint.y + topLeftChamfer] });
      data.push({ mode: 'l', data: [+topLeftChamfer, -topLeftChamfer] });
    }
    // masons mitre female
    else if (
      topLeftMasonsMitreFemaleParams.length > 0 &&
      panel.panelOutline.topLeft.mode === AngleMode.MasonsMitreFemale
    ) {
      const R = topLeftMasonsMitreFemaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (topLeftMasonsMitreFemaleParams.orientation === MasonsMitreOrientationEnum.AlongWidth) {
        data.push({
          mode: 'M',
          data: [this.startDrawPoint.x, +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.length],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.allowance - proectionLengthToAxis,
            this.startDrawPoint.y +
              topLeftMasonsMitreFemaleParams.length -
              topLeftMasonsMitreFemaleParams.allowance +
              proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.length - topLeftMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y +
              topLeftMasonsMitreFemaleParams.length -
              topLeftMasonsMitreFemaleParams.allowance -
              roundSideLength,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.allowance, this.startDrawPoint.y],
        });
      } else {
        data.push({
          mode: 'M',
          data: [this.startDrawPoint.x, +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.allowance],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x +
              topLeftMasonsMitreFemaleParams.length -
              topLeftMasonsMitreFemaleParams.allowance -
              roundSideLength,
            +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.allowance,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.length - topLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x +
              topLeftMasonsMitreFemaleParams.length -
              topLeftMasonsMitreFemaleParams.allowance +
              proectionLengthToAxis,
            +this.startDrawPoint.y + topLeftMasonsMitreFemaleParams.allowance - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + topLeftMasonsMitreFemaleParams.length, +this.startDrawPoint.y],
        });
      }
    }
    // masons mitre male
    else if (panel.panelOutline.topLeft.mode === AngleMode.MasonsMitreMale) {
      const R = topLeftMasonsMitreMaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (topLeftMasonsMitreMaleParams.orientation === MasonsMitreOrientationEnum.AlongWidth) {
        data.push({
          mode: 'M',
          data: [
            this.startDrawPoint.x,
            +this.startDrawPoint.y + topLeftMasonsMitreMaleParams.allowance + roundSideLength,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x,
            +this.startDrawPoint.y + topLeftMasonsMitreMaleParams.allowance,
            this.startDrawPoint.x + proectionLengthToAxis,
            +this.startDrawPoint.y + topLeftMasonsMitreMaleParams.allowance - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + topLeftMasonsMitreMaleParams.allowance, this.startDrawPoint.y],
        });
      } else {
        data.push({
          mode: 'M',
          data: [this.startDrawPoint.x, +this.startDrawPoint.y + topLeftMasonsMitreMaleParams.allowance],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + topLeftMasonsMitreMaleParams.allowance - proectionLengthToAxis,
            +this.startDrawPoint.y + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + topLeftMasonsMitreMaleParams.allowance,
            +this.startDrawPoint.y,
            this.startDrawPoint.x + topLeftMasonsMitreMaleParams.allowance + roundSideLength,
            +this.startDrawPoint.y,
          ],
        });
      }
    }
    // cut insight with angle
    else if (
      +topLeftCutInsideWithAngleLength.x * +topLeftCutInsideWithAngleLength.y > 0 &&
      Math.min(+topLeftCutInsideWithAngleLength.x, +topLeftCutInsideWithAngleLength.y) >=
        topLeftCutInsideWithAngleRadius &&
      panel.panelOutline.topLeft.cutInsideWithAngle.xA * panel.panelOutline.topLeft.cutInsideWithAngle.xA > 0 &&
      panel.panelOutline.topLeft.mode === AngleMode.CuttedInsideWithAngle
    ) {
      const rounding = _.mapValues(
        CuttingPanelHelper.insideCut.getRoundingParams(
          panel.panelOutline.topLeft.cutInsideWithAngle.x,
          panel.panelOutline.topLeft.cutInsideWithAngle.y,
          panel.panelOutline.topLeft.cutInsideWithAngle.r,
          panel.panelOutline.topLeft.cutInsideWithAngle.xA,
          panel.panelOutline.topLeft.cutInsideWithAngle.yA
        ),
        v => v * scale
      );

      [rounding.cx, rounding.x2, rounding.x3] = [rounding.cx, rounding.x2, rounding.x3].map(
        v => v + this.startDrawPoint.x
      );
      [rounding.cy, rounding.y2, rounding.y3] = [rounding.cy, rounding.y2, rounding.y3].map(
        v => v + this.startDrawPoint.y
      );

      data.push({
        mode: 'M',
        data: [this.startDrawPoint.x, +this.startDrawPoint.y + +topLeftCutInsideWithAngleLength.y],
      });
      data.push({
        mode: 'L',
        data: [rounding.x2, rounding.y2],
      });

      data.push({
        mode: 'A',
        data: [
          panel.panelOutline.topLeft.cutInsideWithAngle.r * scale,
          panel.panelOutline.topLeft.cutInsideWithAngle.r * scale,
          0,
          0,
          0,
          rounding.x3,
          rounding.y3,
        ],
      });
      data.push({
        mode: 'L',
        data: [this.startDrawPoint.x + topLeftCutInsideWithAngleLength.x, this.startDrawPoint.y],
      });
    } else {
      data.push({ mode: 'M', data: [this.startDrawPoint.x, this.startDrawPoint.y] });
    }
    /* / TOP LEFT ANGLE */

    /* TOP RIGHT ANGLE */
    if (topRightRounding > 0 && panel.panelOutline.topRight.mode === AngleMode.Rounded) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightRounding, this.startDrawPoint.y],
      });
      data.push({
        mode: 'a',
        data: [topRightRounding, topRightRounding, 0, 0, 1, topRightRounding, topRightRounding],
      });
    }
    // cut
    else if (+topRightCutLength.x * +topRightCutLength.y > 0 && panel.panelOutline.topRight.mode === AngleMode.Cutted) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightCutLength.x, this.startDrawPoint.y],
      });
      data.push({ mode: 'l', data: [topRightCutLength.x, topRightCutLength.y] });
    }
    // rounding inside
    else if (+topRightRoundingInside > 0 && panel.panelOutline.topRight.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightRoundingInside, this.startDrawPoint.y],
      });
      data.push({
        mode: 'a',
        data: [topRightRoundingInside, topRightRoundingInside, 0, 0, 0, topRightRoundingInside, topRightRoundingInside],
      });
    }
    // diagonal cut
    else if (
      panel.panelOutline.topRight.mode === AngleMode.DiagonalCut &&
      panel.panelOutline.topLeft.mode === AngleMode.None &&
      panel.panelOutline.bottomRight.mode === AngleMode.None &&
      panel.panelOutline.bottomLeft.mode !== AngleMode.DiagonalCut
    ) {
      data.push({ mode: 'L', data: [+this.startDrawPoint.x, +this.startDrawPoint.y] });
      data.push({ mode: 'l', data: [+panelHeight, +panelWidth] });
    }
    // inside cut
    else if (
      +topRightCutInsideLength.x * +topRightCutInsideLength.y > 0 &&
      Math.min(+topRightCutInsideLength.x, +topRightCutInsideLength.y) >= topRightCutInsideRadius &&
      panel.panelOutline.topRight.mode === AngleMode.CuttedInside
    ) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightCutInsideLength.x, this.startDrawPoint.y],
      });
      data.push({ mode: 'l', data: [0, topRightCutInsideLength.y - topRightCutInsideRadius] });
      data.push({
        mode: 'q',
        data: [0, +topRightCutInsideRadius, +topRightCutInsideRadius, +topRightCutInsideRadius],
      });
      data.push({ mode: 'l', data: [topRightCutInsideLength.x - topRightCutInsideRadius, 0] });
    }
    // chamfer
    else if (topRightChamfer > 0 && panel.panelOutline.topRight.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightChamfer, +this.startDrawPoint.y],
      });
      data.push({ mode: 'l', data: [+topRightChamfer, +topRightChamfer] });
    }
    // masons mitre female
    else if (
      topRightMasonsMitreFemaleParams.length > 0 &&
      panel.panelOutline.topRight.mode === AngleMode.MasonsMitreFemale
    ) {
      const R = topRightMasonsMitreFemaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (topRightMasonsMitreFemaleParams.orientation === MasonsMitreOrientationEnum.AlongHeight) {
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + panelHeight - topRightMasonsMitreFemaleParams.length, +this.startDrawPoint.y],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x +
              panelHeight -
              topRightMasonsMitreFemaleParams.length +
              topRightMasonsMitreFemaleParams.allowance -
              proectionLengthToAxis,
            this.startDrawPoint.y + topRightMasonsMitreFemaleParams.allowance - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x +
              panelHeight -
              topRightMasonsMitreFemaleParams.length +
              topRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.y + topRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x +
              panelHeight -
              topRightMasonsMitreFemaleParams.length +
              topRightMasonsMitreFemaleParams.allowance +
              roundSideLength,
            this.startDrawPoint.y + topRightMasonsMitreFemaleParams.allowance,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight,
            this.startDrawPoint.y + topRightMasonsMitreFemaleParams.allowance,
          ],
        });
      } else {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y +
              topRightMasonsMitreFemaleParams.length -
              topRightMasonsMitreFemaleParams.allowance -
              roundSideLength,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y + topRightMasonsMitreFemaleParams.length - topRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreFemaleParams.allowance + proectionLengthToAxis,
            +this.startDrawPoint.y +
              topRightMasonsMitreFemaleParams.length -
              topRightMasonsMitreFemaleParams.allowance +
              proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + panelHeight, +this.startDrawPoint.y + topRightMasonsMitreFemaleParams.length],
        });
      }
    }
    // masons mitre male
    else if (panel.panelOutline.topRight.mode === AngleMode.MasonsMitreMale) {
      const R = topRightMasonsMitreMaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (topRightMasonsMitreMaleParams.orientation === MasonsMitreOrientationEnum.AlongHeight) {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreMaleParams.allowance - roundSideLength,
            +this.startDrawPoint.y,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreMaleParams.allowance,
            +this.startDrawPoint.y,
            this.startDrawPoint.x + panelHeight - topRightMasonsMitreMaleParams.allowance + proectionLengthToAxis,
            +this.startDrawPoint.y + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + panelHeight, this.startDrawPoint.y + topRightMasonsMitreMaleParams.allowance],
        });
      } else {
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + panelHeight - topRightMasonsMitreMaleParams.allowance, +this.startDrawPoint.y],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - proectionLengthToAxis,
            +this.startDrawPoint.y + topRightMasonsMitreMaleParams.allowance - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + topRightMasonsMitreMaleParams.allowance,
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + topRightMasonsMitreMaleParams.allowance + roundSideLength,
          ],
        });
      }
    }
    // cut insight with angle
    else if (
      +topRightCutInsideWithAngleLength.x * +topRightCutInsideWithAngleLength.y > 0 &&
      Math.min(+topRightCutInsideWithAngleLength.x, +topRightCutInsideWithAngleLength.y) >=
        topRightCutInsideWithAngleRadius &&
      panel.panelOutline.topRight.cutInsideWithAngle.xA * panel.panelOutline.topRight.cutInsideWithAngle.xA > 0 &&
      panel.panelOutline.topRight.mode === AngleMode.CuttedInsideWithAngle
    ) {
      const rounding = _.mapValues(
        CuttingPanelHelper.insideCut.getRoundingParams(
          panel.panelOutline.topRight.cutInsideWithAngle.x,
          panel.panelOutline.topRight.cutInsideWithAngle.y,
          panel.panelOutline.topRight.cutInsideWithAngle.r,
          panel.panelOutline.topRight.cutInsideWithAngle.xA,
          panel.panelOutline.topRight.cutInsideWithAngle.yA
        ),
        v => v * scale
      );

      [rounding.cx, rounding.x2, rounding.x3] = [rounding.cx, rounding.x2, rounding.x3].map(
        v => this.mirrorCoord(panelHeight / 2, v) + this.startDrawPoint.x
      );
      [rounding.cy, rounding.y2, rounding.y3] = [rounding.cy, rounding.y2, rounding.y3].map(
        v => v + this.startDrawPoint.y
      );

      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight - +topRightCutInsideWithAngleLength.x, this.startDrawPoint.y],
      });
      data.push({
        mode: 'L',
        data: [rounding.x3, rounding.y3],
      });
      data.push({
        mode: 'A',
        data: [
          panel.panelOutline.topRight.cutInsideWithAngle.r * scale,
          panel.panelOutline.topRight.cutInsideWithAngle.r * scale,
          0,
          0,
          0,
          rounding.x2,
          rounding.y2,
        ],
      });
      data.push({
        mode: 'L',
        data: [this.startDrawPoint.x + panelHeight, this.startDrawPoint.y + topRightCutInsideWithAngleLength.y],
      });
    } else {
      data.push({ mode: 'L', data: [+this.startDrawPoint.x + +panelHeight, this.startDrawPoint.y] });
    }
    /* / TOP RIGHT ANGLE */

    /* BOTTOM RIGHT ANGLE */
    if (bottomRightRounding > 0 && panel.panelOutline.bottomRight.mode === AngleMode.Rounded) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y + +panelWidth - +bottomRightRounding],
      });
      data.push({
        mode: 'a',
        data: [bottomRightRounding, bottomRightRounding, 0, 0, 1, -bottomRightRounding, bottomRightRounding],
      });
    }
    // cut
    else if (
      +bottomRightCutLength.x * +bottomRightCutLength.y > 0 &&
      panel.panelOutline.bottomRight.mode === AngleMode.Cutted
    ) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y + +panelWidth - +bottomRightCutLength.y],
      });
      data.push({ mode: 'l', data: [-bottomRightCutLength.x, bottomRightCutLength.y] });
    }
    // rounding inside
    else if (+bottomRightRoundingInside > 0 && panel.panelOutline.bottomRight.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'L',
        data: [
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightRoundingInside,
        ],
      });
      data.push({
        mode: 'a',
        data: [
          bottomRightRoundingInside,
          bottomRightRoundingInside,
          0,
          0,
          0,
          -bottomRightRoundingInside,
          +bottomRightRoundingInside,
        ],
      });
    }
    // diagonal cut
    else if (
      panel.panelOutline.bottomRight.mode === AngleMode.DiagonalCut &&
      panel.panelOutline.topRight.mode === AngleMode.None &&
      panel.panelOutline.bottomLeft.mode === AngleMode.None &&
      panel.panelOutline.topLeft.mode !== AngleMode.DiagonalCut
    ) {
      data.push({ mode: 'L', data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y] });
      data.push({ mode: 'l', data: [-panelHeight, +panelWidth] });
    }
    // inside cut
    else if (
      +bottomRightCutInsideLength.x * +bottomRightCutInsideLength.y > 0 &&
      Math.min(+bottomRightCutInsideLength.x, +bottomRightCutInsideLength.y) >= bottomRightCutInsideRadius &&
      panel.panelOutline.bottomRight.mode === AngleMode.CuttedInside
    ) {
      data.push({
        mode: 'L',
        data: [
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightCutInsideLength.y,
        ],
      });
      data.push({ mode: 'l', data: [-bottomRightCutInsideLength.x + bottomRightCutInsideRadius, 0] });
      data.push({
        mode: 'q',
        data: [-bottomRightCutInsideRadius, 0, -bottomRightCutInsideRadius, +bottomRightCutInsideRadius],
      });
      data.push({ mode: 'l', data: [0, bottomRightCutInsideLength.y - bottomRightCutInsideRadius] });
    }
    // chamfer
    else if (bottomRightChamfer > 0 && panel.panelOutline.bottomRight.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y + +panelWidth - +bottomRightChamfer],
      });
      data.push({ mode: 'l', data: [-bottomRightChamfer, +bottomRightChamfer] });
    }
    // masons mitre female
    else if (
      bottomRightMasonsMitreFemaleParams.length > 0 &&
      panel.panelOutline.bottomRight.mode === AngleMode.MasonsMitreFemale
    ) {
      const R = bottomRightMasonsMitreFemaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (bottomRightMasonsMitreFemaleParams.orientation === MasonsMitreOrientationEnum.AlongWidth) {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreFemaleParams.length,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreFemaleParams.allowance + proectionLengthToAxis,
            this.startDrawPoint.y +
              panelWidth -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance -
              proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.y +
              panelWidth -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.y +
              panelWidth -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance +
              roundSideLength,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.y + panelWidth,
          ],
        });
      } else {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreFemaleParams.allowance,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x +
              panelHeight -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance +
              roundSideLength,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreFemaleParams.allowance,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x +
              panelHeight -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x +
              panelHeight -
              bottomRightMasonsMitreFemaleParams.length +
              bottomRightMasonsMitreFemaleParams.allowance -
              proectionLengthToAxis,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreFemaleParams.allowance + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreFemaleParams.length,
            +this.startDrawPoint.y + panelWidth,
          ],
        });
      }
    }
    // masons mitre male
    else if (panel.panelOutline.bottomRight.mode === AngleMode.MasonsMitreMale) {
      const R = bottomRightMasonsMitreMaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (bottomRightMasonsMitreMaleParams.orientation === MasonsMitreOrientationEnum.AlongWidth) {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreMaleParams.allowance - roundSideLength,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreMaleParams.allowance,
            this.startDrawPoint.x + panelHeight - proectionLengthToAxis,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreMaleParams.allowance + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreMaleParams.allowance,
            this.startDrawPoint.y + panelWidth,
          ],
        });
      } else {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight,
            +this.startDrawPoint.y + panelWidth - bottomRightMasonsMitreMaleParams.allowance,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreMaleParams.allowance + proectionLengthToAxis,
            this.startDrawPoint.y + panelWidth - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreMaleParams.allowance,
            this.startDrawPoint.y + panelWidth,
            this.startDrawPoint.x + panelHeight - bottomRightMasonsMitreMaleParams.allowance - roundSideLength,
            this.startDrawPoint.y + panelWidth,
          ],
        });
      }
    }
    // cut insight with angle
    else if (
      +bottomRightCutInsideWithAngleLength.x * +bottomRightCutInsideWithAngleLength.y > 0 &&
      Math.min(+bottomRightCutInsideWithAngleLength.x, +bottomRightCutInsideWithAngleLength.y) >=
        bottomRightCutInsideWithAngleRadius &&
      panel.panelOutline.bottomRight.cutInsideWithAngle.xA * panel.panelOutline.bottomRight.cutInsideWithAngle.xA > 0 &&
      panel.panelOutline.bottomRight.mode === AngleMode.CuttedInsideWithAngle
    ) {
      const rounding = _.mapValues(
        CuttingPanelHelper.insideCut.getRoundingParams(
          panel.panelOutline.bottomRight.cutInsideWithAngle.x,
          panel.panelOutline.bottomRight.cutInsideWithAngle.y,
          panel.panelOutline.bottomRight.cutInsideWithAngle.r,
          panel.panelOutline.bottomRight.cutInsideWithAngle.xA,
          panel.panelOutline.bottomRight.cutInsideWithAngle.yA
        ),
        v => v * scale
      );

      [rounding.cx, rounding.x2, rounding.x3] = [rounding.cx, rounding.x2, rounding.x3].map(
        v => this.mirrorCoord(panelHeight / 2, v) + this.startDrawPoint.x
      );
      [rounding.cy, rounding.y2, rounding.y3] = [rounding.cy, rounding.y2, rounding.y3].map(
        v => this.mirrorCoord(panelWidth / 2, v) + this.startDrawPoint.y
      );

      data.push({
        mode: 'L',
        data: [
          +this.startDrawPoint.x + +panelHeight,
          this.startDrawPoint.y + panelWidth - bottomRightCutInsideWithAngleLength.y,
        ],
      });

      data.push({
        mode: 'L',
        data: [rounding.x2, rounding.y2],
      });
      data.push({
        mode: 'A',
        data: [
          panel.panelOutline.bottomRight.cutInsideWithAngle.r * scale,
          panel.panelOutline.bottomRight.cutInsideWithAngle.r * scale,
          0,
          0,
          0,
          rounding.x3,
          rounding.y3,
        ],
      });
      data.push({
        mode: 'L',
        data: [
          this.startDrawPoint.x + panelHeight - bottomRightCutInsideWithAngleLength.x,
          this.startDrawPoint.y + panelWidth,
        ],
      });
    } else {
      data.push({ mode: 'L', data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y + +panelWidth] });
    }
    /* / BOTTOM RIGHT ANGLE */

    /* BOTTOM LEFT ANGLE */
    if (bottomLeftRounding > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.Rounded) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +bottomLeftRounding, +this.startDrawPoint.y + +panelWidth],
      });
      data.push({
        mode: 'a',
        data: [bottomLeftRounding, bottomLeftRounding, 0, 0, 1, -bottomLeftRounding, -bottomLeftRounding],
      });
    }
    // cut
    else if (
      +bottomLeftCutLength.x * +bottomLeftCutLength.y > 0 &&
      panel.panelOutline.bottomLeft.mode === AngleMode.Cutted
    ) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +bottomLeftCutLength.x, +this.startDrawPoint.y + +panelWidth],
      });
      data.push({ mode: 'l', data: [-bottomLeftCutLength.x, -bottomLeftCutLength.y] });
    }
    // rounding inside
    else if (+bottomLeftRoundingInside > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +bottomLeftRoundingInside, +this.startDrawPoint.y + +panelWidth],
      });
      data.push({
        mode: 'a',
        data: [
          bottomLeftRoundingInside,
          bottomLeftRoundingInside,
          0,
          0,
          0,
          -bottomLeftRoundingInside,
          -bottomLeftRoundingInside,
        ],
      });
    }
    // diagonal cut
    else if (
      panel.panelOutline.bottomLeft.mode === AngleMode.DiagonalCut &&
      panel.panelOutline.topLeft.mode === AngleMode.None &&
      panel.panelOutline.bottomRight.mode === AngleMode.None &&
      panel.panelOutline.topRight.mode !== AngleMode.DiagonalCut
    ) {
      data.push({ mode: 'L', data: [+this.startDrawPoint.x + +panelHeight, +this.startDrawPoint.y + +panelWidth] });
      data.push({ mode: 'l', data: [-panelHeight, -panelWidth] });
    }
    // inside cut
    else if (
      +bottomLeftCutInsideLength.x * +bottomLeftCutInsideLength.y > 0 &&
      Math.min(+bottomLeftCutInsideLength.x, +bottomLeftCutInsideLength.y) >= bottomLeftCutInsideRadius &&
      panel.panelOutline.bottomLeft.mode === AngleMode.CuttedInside
    ) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +bottomLeftCutInsideLength.x, +this.startDrawPoint.y + +panelWidth],
      });
      data.push({ mode: 'l', data: [0, -bottomLeftCutInsideLength.y + bottomLeftCutInsideRadius] });
      data.push({
        mode: 'q',
        data: [0, -bottomLeftCutInsideRadius, -bottomLeftCutInsideRadius, -bottomLeftCutInsideRadius],
      });
      data.push({ mode: 'l', data: [-bottomLeftCutInsideLength.x + bottomLeftCutInsideRadius, 0] });
    }
    // chamfer
    else if (bottomLeftChamfer > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + +bottomLeftChamfer, +this.startDrawPoint.y + +panelWidth],
      });
      data.push({ mode: 'l', data: [-bottomLeftChamfer, -bottomLeftChamfer] });
    }
    // masons mitre female
    else if (
      bottomLeftMasonsMitreFemaleParams.length > 0 &&
      panel.panelOutline.bottomLeft.mode === AngleMode.MasonsMitreFemale
    ) {
      const R = bottomLeftMasonsMitreFemaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (bottomLeftMasonsMitreFemaleParams.orientation === MasonsMitreOrientationEnum.AlongHeight) {
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x + bottomLeftMasonsMitreFemaleParams.length, +this.startDrawPoint.y + panelWidth],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x +
              bottomLeftMasonsMitreFemaleParams.length -
              bottomLeftMasonsMitreFemaleParams.allowance +
              proectionLengthToAxis,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreFemaleParams.allowance + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x +
              bottomLeftMasonsMitreFemaleParams.length -
              bottomLeftMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x +
              bottomLeftMasonsMitreFemaleParams.length -
              bottomLeftMasonsMitreFemaleParams.allowance -
              roundSideLength,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreFemaleParams.allowance,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreFemaleParams.allowance,
          ],
        });
      } else {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y + panelWidth,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y +
              panelWidth -
              bottomLeftMasonsMitreFemaleParams.length +
              bottomLeftMasonsMitreFemaleParams.allowance +
              roundSideLength,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreFemaleParams.allowance,
            +this.startDrawPoint.y +
              panelWidth -
              bottomLeftMasonsMitreFemaleParams.length +
              bottomLeftMasonsMitreFemaleParams.allowance,
            this.startDrawPoint.x + bottomLeftMasonsMitreFemaleParams.allowance - proectionLengthToAxis,
            +this.startDrawPoint.y +
              panelWidth -
              bottomLeftMasonsMitreFemaleParams.length +
              bottomLeftMasonsMitreFemaleParams.allowance -
              proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x, +this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreFemaleParams.length],
        });
      }
    }
    // masons mitre male
    else if (panel.panelOutline.bottomLeft.mode === AngleMode.MasonsMitreMale) {
      const R = bottomLeftMasonsMitreMaleParams.radius;
      const inclineDeg = 135;
      const bisectorDeg = inclineDeg / 2;
      const roundSideLength = R / Math.tan(this.deg2rad(bisectorDeg));
      const proectionLengthToAxis = Math.cos(this.deg2rad(45)) * roundSideLength;

      if (bottomLeftMasonsMitreMaleParams.orientation === MasonsMitreOrientationEnum.AlongHeight) {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreMaleParams.allowance + roundSideLength,
            +this.startDrawPoint.y + panelWidth,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreMaleParams.allowance,
            +this.startDrawPoint.y + panelWidth,
            this.startDrawPoint.x + bottomLeftMasonsMitreMaleParams.allowance - proectionLengthToAxis,
            +this.startDrawPoint.y + panelWidth - proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'L',
          data: [this.startDrawPoint.x, this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreMaleParams.allowance],
        });
      } else {
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + bottomLeftMasonsMitreMaleParams.allowance,
            +this.startDrawPoint.y + panelWidth,
          ],
        });
        data.push({
          mode: 'L',
          data: [
            this.startDrawPoint.x + proectionLengthToAxis,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreMaleParams.allowance + proectionLengthToAxis,
          ],
        });
        data.push({
          mode: 'Q',
          data: [
            this.startDrawPoint.x,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreMaleParams.allowance,
            this.startDrawPoint.x,
            this.startDrawPoint.y + panelWidth - bottomLeftMasonsMitreMaleParams.allowance - roundSideLength,
          ],
        });
      }
    }
    // cut insight with angle
    else if (
      +bottomLeftCutInsideWithAngleLength.x * +bottomLeftCutInsideWithAngleLength.y > 0 &&
      Math.min(+bottomLeftCutInsideWithAngleLength.x, +bottomLeftCutInsideWithAngleLength.y) >=
        bottomLeftCutInsideWithAngleRadius &&
      panel.panelOutline.bottomLeft.cutInsideWithAngle.xA * panel.panelOutline.bottomLeft.cutInsideWithAngle.xA > 0 &&
      panel.panelOutline.bottomLeft.mode === AngleMode.CuttedInsideWithAngle
    ) {
      const rounding = _.mapValues(
        CuttingPanelHelper.insideCut.getRoundingParams(
          panel.panelOutline.bottomLeft.cutInsideWithAngle.x,
          panel.panelOutline.bottomLeft.cutInsideWithAngle.y,
          panel.panelOutline.bottomLeft.cutInsideWithAngle.r,
          panel.panelOutline.bottomLeft.cutInsideWithAngle.xA,
          panel.panelOutline.bottomLeft.cutInsideWithAngle.yA
        ),
        v => v * scale
      );

      [rounding.cx, rounding.x2, rounding.x3] = [rounding.cx, rounding.x2, rounding.x3].map(
        v => v + this.startDrawPoint.x
      );
      [rounding.cy, rounding.y2, rounding.y3] = [rounding.cy, rounding.y2, rounding.y3].map(
        v => this.mirrorCoord(panelWidth / 2, v) + this.startDrawPoint.y
      );

      data.push({
        mode: 'L',
        data: [+this.startDrawPoint.x + bottomLeftCutInsideWithAngleLength.x, this.startDrawPoint.y + panelWidth],
      });

      data.push({
        mode: 'L',
        data: [rounding.x3, rounding.y3],
      });
      data.push({
        mode: 'A',
        data: [
          panel.panelOutline.bottomLeft.cutInsideWithAngle.r * scale,
          panel.panelOutline.bottomLeft.cutInsideWithAngle.r * scale,
          0,
          0,
          0,
          rounding.x2,
          rounding.y2,
        ],
      });
      data.push({
        mode: 'L',
        data: [this.startDrawPoint.x, this.startDrawPoint.y + panelWidth - bottomLeftCutInsideWithAngleLength.y],
      });
    } else {
      data.push({ mode: 'L', data: [this.startDrawPoint.x, +this.startDrawPoint.y + +panelWidth] });
    }
    /* / BOTTOM LEFT ANGLE */

    data.push({ mode: 'z', data: [] });

    return data;
  }

  getInfoLines(panel: PanelBase): PathData[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;
    const topLeftRounding: number = panel.panelOutline.topLeft.rounding * scale;
    const topRightRounding: number = panel.panelOutline.topRight.rounding * scale;
    const bottomLeftRounding: number = panel.panelOutline.bottomLeft.rounding * scale;
    const bottomRightRounding: number = panel.panelOutline.bottomRight.rounding * scale;
    const topLeftRoundingInside: number = panel.panelOutline.topLeft.roundingInside * scale;
    const topRightRoundingInside: number = panel.panelOutline.topRight.roundingInside * scale;
    const bottomLeftRoundingInside: number = panel.panelOutline.bottomLeft.roundingInside * scale;
    const bottomRightRoundingInside: number = panel.panelOutline.bottomRight.roundingInside * scale;

    const topLeftChamfer: number = panel.panelOutline.topLeft.chamferLength * scale;
    const topRightChamfer: number = panel.panelOutline.topRight.chamferLength * scale;
    const bottomRightChamfer: number = panel.panelOutline.bottomRight.chamferLength * scale;
    const bottomLeftChamfer: number = panel.panelOutline.bottomLeft.chamferLength * scale;

    const topLeftCutLength: Point = {
      x: panel.panelOutline.topLeft.cutX * scale,
      y: panel.panelOutline.topLeft.cutY * scale,
    };
    const topRightCutLength: Point = {
      x: panel.panelOutline.topRight.cutX * scale,
      y: panel.panelOutline.topRight.cutY * scale,
    };
    const bottomLeftCutLength: Point = {
      x: panel.panelOutline.bottomLeft.cutX * scale,
      y: panel.panelOutline.bottomLeft.cutY * scale,
    };
    const bottomRightCutLength: Point = {
      x: panel.panelOutline.bottomRight.cutX * scale,
      y: panel.panelOutline.bottomRight.cutY * scale,
    };

    const data: PathData[] = [];
    /* TOP LEFT ANGLE */
    if (topLeftRounding > 0 && panel.panelOutline.topLeft.mode === AngleMode.Rounded) {
      data.push({
        mode: 'info-line-rounding-top-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +topLeftRounding,
          +this.startDrawPoint.x + +topLeftRounding,
          +this.startDrawPoint.y + +topLeftRounding,
          +this.startDrawPoint.x + +topLeftRounding,
          +this.startDrawPoint.y,
        ],
      });
    } else if (+topLeftCutLength.x * +topLeftCutLength.y > 0 && panel.panelOutline.topLeft.mode === AngleMode.Cutted) {
      data.push({
        mode: 'info-line-cut-top-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +topLeftCutLength.y,
          +this.startDrawPoint.x + +topLeftCutLength.x,
          +this.startDrawPoint.y + +topLeftCutLength.y,
          +this.startDrawPoint.x + +topLeftCutLength.x,
          +this.startDrawPoint.y,
        ],
      });
    } else if (topLeftRoundingInside > 0 && panel.panelOutline.topLeft.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'info-line-rounding-inside-top-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +topLeftRoundingInside,
          +this.startDrawPoint.x + +topLeftRoundingInside,
          +this.startDrawPoint.y + +topLeftRoundingInside,
          +this.startDrawPoint.x + +topLeftRoundingInside,
          +this.startDrawPoint.y,
        ],
      });
    } else if (+topLeftChamfer > 0 && panel.panelOutline.topLeft.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'info-line-chamfer-top-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +topLeftChamfer,
          +this.startDrawPoint.x + +topLeftChamfer,
          +this.startDrawPoint.y + +topLeftChamfer,
          +this.startDrawPoint.x + +topLeftChamfer,
          +this.startDrawPoint.y,
        ],
      });
    } else {
      data.push({
        mode: 'info-line-rounding-top-left',
        data: [],
      });
      data.push({
        mode: 'info-line-cut-top-left',
        data: [],
      });
      data.push({
        mode: 'info-line-rounding-inside-top-left',
        data: [],
      });
      data.push({
        mode: 'info-line-chamfer-top-left',
        data: [],
      });
    }
    /* / TOP LEFT ANGLE */

    /* TOP RIGHT ANGLE */
    if (topRightRounding > 0 && panel.panelOutline.topRight.mode === AngleMode.Rounded) {
      data.push({
        mode: 'info-line-rounding-top-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +topRightRounding,
          +this.startDrawPoint.y,
          +this.startDrawPoint.x + +panelHeight - +topRightRounding,
          +this.startDrawPoint.y + +topRightRounding,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +topRightRounding,
        ],
      });
    } else if (
      +topRightCutLength.x * +topRightCutLength.y > 0 &&
      panel.panelOutline.topRight.mode === AngleMode.Cutted
    ) {
      data.push({
        mode: 'info-line-cut-top-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +topRightCutLength.x,
          +this.startDrawPoint.y,
          +this.startDrawPoint.x + +panelHeight - +topRightCutLength.x,
          +this.startDrawPoint.y + +topRightCutLength.y,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +topRightCutLength.y,
        ],
      });
    } else if (topRightRoundingInside > 0 && panel.panelOutline.topRight.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'info-line-rounding-inside-top-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +topRightRoundingInside,
          +this.startDrawPoint.y,
          +this.startDrawPoint.x + +panelHeight - +topRightRoundingInside,
          +this.startDrawPoint.y + +topRightRoundingInside,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +topRightRoundingInside,
        ],
      });
    } else if (+topRightChamfer > 0 && panel.panelOutline.topRight.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'info-line-chamfer-top-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +topRightChamfer,
          +this.startDrawPoint.y,
          +this.startDrawPoint.x + +panelHeight - +topRightChamfer,
          +this.startDrawPoint.y + +topRightChamfer,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +topRightChamfer,
        ],
      });
    } else {
      data.push({
        mode: 'info-line-rounding-top-right',
        data: [],
      });
      data.push({
        mode: 'info-line-cut-top-right',
        data: [],
      });
      data.push({
        mode: 'info-line-rounding-inside-top-right',
        data: [],
      });
      data.push({
        mode: 'info-line-chamfer-top-right',
        data: [],
      });
    }
    /* / TOP RIGHT ANGLE */

    /* BOTTOM RIGHT ANGLE */
    if (bottomRightRounding > 0 && panel.panelOutline.bottomRight.mode === AngleMode.Rounded) {
      data.push({
        mode: 'info-line-rounding-bottom-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +bottomRightRounding,
          +this.startDrawPoint.y + +panelWidth,
          +this.startDrawPoint.x + +panelHeight - +bottomRightRounding,
          +this.startDrawPoint.y + +panelWidth - +bottomRightRounding,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightRounding,
        ],
      });
    } else if (
      +bottomRightCutLength.x * +bottomRightCutLength.y > 0 &&
      panel.panelOutline.bottomRight.mode === AngleMode.Cutted
    ) {
      data.push({
        mode: 'info-line-cut-bottom-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +bottomRightCutLength.x,
          +this.startDrawPoint.y + +panelWidth,
          +this.startDrawPoint.x + +panelHeight - +bottomRightCutLength.x,
          +this.startDrawPoint.y + +panelWidth - +bottomRightCutLength.y,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightCutLength.y,
        ],
      });
    } else if (bottomRightRoundingInside > 0 && panel.panelOutline.bottomRight.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'info-line-rounding-inside-bottom-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +bottomRightRoundingInside,
          +this.startDrawPoint.y + +panelWidth,
          +this.startDrawPoint.x + +panelHeight - +bottomRightRoundingInside,
          +this.startDrawPoint.y + +panelWidth - +bottomRightRoundingInside,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightRoundingInside,
        ],
      });
    } else if (+bottomRightChamfer > 0 && panel.panelOutline.bottomRight.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'info-line-chamfer-bottom-right',
        data: [
          +this.startDrawPoint.x + +panelHeight - +bottomRightChamfer,
          +this.startDrawPoint.y + +panelWidth,
          +this.startDrawPoint.x + +panelHeight - +bottomRightChamfer,
          +this.startDrawPoint.y + +panelWidth - +bottomRightChamfer,
          +this.startDrawPoint.x + +panelHeight,
          +this.startDrawPoint.y + +panelWidth - +bottomRightChamfer,
        ],
      });
    } else {
      data.push({
        mode: 'info-line-rounding-bottom-right',
        data: [],
      });
      data.push({
        mode: 'info-line-cut-bottom-right',
        data: [],
      });
      data.push({
        mode: 'info-line-rounding-inside-bottom-right',
        data: [],
      });
      data.push({
        mode: 'info-line-chamfer-bottom-right',
        data: [],
      });
    }
    /* / BOTTOM RIGHT ANGLE */

    /* BOTTOM LEFT ANGLE */
    if (bottomLeftRounding > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.Rounded) {
      data.push({
        mode: 'info-line-rounding-bottom-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftRounding,
          +this.startDrawPoint.x + +bottomLeftRounding,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftRounding,
          +this.startDrawPoint.x + +bottomLeftRounding,
          +this.startDrawPoint.y + +panelWidth,
        ],
      });
    } else if (bottomLeftRoundingInside > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.RoundedInside) {
      data.push({
        mode: 'info-line-rounding-inside-bottom-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftRoundingInside,
          +this.startDrawPoint.x + +bottomLeftRoundingInside,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftRoundingInside,
          +this.startDrawPoint.x + +bottomLeftRoundingInside,
          +this.startDrawPoint.y + +panelWidth,
        ],
      });
    } else if (
      +bottomLeftCutLength.x * +bottomLeftCutLength.y > 0 &&
      panel.panelOutline.bottomLeft.mode === AngleMode.Cutted
    ) {
      data.push({
        mode: 'info-line-cut-bottom-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftCutLength.y,
          +this.startDrawPoint.x + +bottomLeftCutLength.x,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftCutLength.y,
          +this.startDrawPoint.x + +bottomLeftCutLength.x,
          +this.startDrawPoint.y + +panelWidth,
        ],
      });
    } else if (+bottomLeftChamfer > 0 && panel.panelOutline.bottomLeft.mode === AngleMode.Chamfer) {
      data.push({
        mode: 'info-line-chamfer-bottom-left',
        data: [
          +this.startDrawPoint.x,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftChamfer,
          +this.startDrawPoint.x + +bottomLeftChamfer,
          +this.startDrawPoint.y + +panelWidth - +bottomLeftChamfer,
          +this.startDrawPoint.x + +bottomLeftChamfer,
          +this.startDrawPoint.y + +panelWidth,
        ],
      });
    } else {
      data.push({
        mode: 'info-line-rounding-bottom-left',
        data: [],
      });
      data.push({
        mode: 'info-line-cut-bottom-left',
        data: [],
      });
      data.push({
        mode: 'info-line-rounding-inside-bottom-left',
        data: [],
      });
      data.push({
        mode: 'info-line-chamfer-bottom-left',
        data: [],
      });
    }
    /* / BOTTOM LEFT ANGLE */

    return data;
  }

  getXYAxisText(panel: PanelBase, axisBase: Point): AxisTextData[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const data: AxisTextData[] = [];

    const textHeight = 15;

    data.push({
      text: 'y+',
      id: 'y-plus-axis',
      x: +this.startDrawPoint.x + axisBase.x * scale + this.textOffset,
      y: +this.startDrawPoint.y + this.textOffset + textHeight,
    });

    data.push({
      text: 'y-',
      id: 'y-minus-axis',
      x: +this.startDrawPoint.x + axisBase.x * scale + this.textOffset,
      y: +this.startDrawPoint.y - +this.textOffset + panelWidth,
    });

    data.push({
      text: 'x-',
      id: 'x-minus-axis',
      x: axisBase.x === 0 ? 0 : +this.startDrawPoint.x + this.textOffset,
      y: +this.startDrawPoint.y + axisBase.y * scale - +this.textOffset,
    });

    data.push({
      text: 'x+',
      id: 'x-plus-axis',
      x: +panelHeight + +this.startDrawPoint.x - +this.textOffset - textHeight,
      y: +this.startDrawPoint.y + axisBase.y * scale - +this.textOffset,
    });

    return data;
  }

  getText(panel: PanelBase): TextData[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const data: TextData[] = [];
    const textLengthPx = 60;

    const angleAdder = this.addAngleText(data, scale, panel.height, panel.width);
    angleAdder(panel.panelOutline.topLeft, 'tl');
    angleAdder(panel.panelOutline.topRight, 'tr');
    angleAdder(panel.panelOutline.bottomRight, 'br');
    angleAdder(panel.panelOutline.bottomLeft, 'bl');

    /* TOP PANEL LINE */
    data.push({
      text: panel.height + 'mm',
      position: 'tc',
      x: +panelHeight / 2 + +this.startDrawPoint.x - +textLengthPx / 2,
      y: +this.startDrawPoint.y - +this.textOffset,
    });

    /* LEFT PANEL LINE */
    data.push({
      text: panel.width + 'mm',
      position: 'lc',
      x: +this.textSize,
      y: +panelWidth / 2 + +this.startDrawPoint.y + +textLengthPx / 2,
    });

    return data;
  }

  getGrooves(panel: PanelBase): RectangleData[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const data: RectangleData[] = [];

    const adder = this.addGroove(scale, panelHeight, panelWidth);
    data.push(adder(panel.panelOutline.topSide.groove, 'tc'));
    data.push(adder(panel.panelOutline.bottomSide.groove, 'bc'));
    data.push(adder(panel.panelOutline.leftSide.groove, 'lc'));
    data.push(adder(panel.panelOutline.rightSide.groove, 'rc'));

    return data;
  }

  getCusttomHoles(panel: PanelBase, highlightedHole?: CuttingCustomHole[] | undefined): HolePathData[] {
    const data: HolePathData[] = [];

    const adder = this._getCusttomHoles(panel.height, panel.width, highlightedHole);

    data.push(...adder(panel.panelOutline.topSide.customHoles, 'tc'));
    data.push(...adder(panel.panelOutline.rightSide.customHoles, 'rc'));
    data.push(...adder(panel.panelOutline.bottomSide.customHoles, 'bc'));
    data.push(...adder(panel.panelOutline.leftSide.customHoles, 'lc'));

    return data;
  }

  async getCutouts(panel: PanelBase): Promise<Array<CutoutPathData | CutoutFragmentData>> {
    const adder = this._getCutouts(panel.height, panel.width);

    return await adder(panel.panelOutline.cutouts);
  }

  getFixedFastenerSets(
    panel: PanelBase,
    highlightedSet?: CuttingFixedFastenersSet | undefined
  ): FastenersSetPathData[] {
    const data: FastenersSetPathData[] = [];

    const adder = this._getFixedFastenerSets(panel.height, panel.width, highlightedSet);

    data.push(...adder(panel.panelOutline.topSide.fastenersSets, 'tc'));
    data.push(...adder(panel.panelOutline.rightSide.fastenersSets, 'rc'));
    data.push(...adder(panel.panelOutline.bottomSide.fastenersSets, 'bc'));
    data.push(...adder(panel.panelOutline.leftSide.fastenersSets, 'lc'));

    return data;
  }

  getBasePointSelectors(panel: PanelBase, side: Position | undefined): PositionedPoint[] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const selectors: PositionedPoint[] = [];

    if (side === 'tc' || side === 'lc' || side === undefined) {
      selectors.push({ x: this.startDrawPoint.x, y: this.startDrawPoint.y, position: 'tl' });
    } else {
      selectors.push({ x: -1000, y: -1000, position: 'tl' });
    }

    if (side === 'rc' || side === 'tc' || side === undefined) {
      selectors.push({ x: this.startDrawPoint.x + panelHeight, y: this.startDrawPoint.y, position: 'tr' });
    } else {
      selectors.push({ x: -1000, y: -1000, position: 'tr' });
    }

    if (side === 'bc' || side === 'rc' || side === undefined) {
      selectors.push({ x: this.startDrawPoint.x + panelHeight, y: this.startDrawPoint.y + panelWidth, position: 'br' });
    } else {
      selectors.push({ x: -1000, y: -1000, position: 'br' });
    }

    if (side === 'lc' || side === 'bc' || side === undefined) {
      selectors.push({ x: this.startDrawPoint.x, y: this.startDrawPoint.y + panelWidth, position: 'bl' });
    } else {
      selectors.push({ x: -1000, y: -1000, position: 'bl' });
    }

    return selectors;
  }

  getXYAxis(base: Point, panelHeightWithoutScaling: number, panelWidthWithoutscaling: number): PathData[] {
    const scale =
      panelWidthWithoutscaling > panelHeightWithoutScaling
        ? this.getPanelScale(panelWidthWithoutscaling, this.height)
        : this.getPanelScale(panelHeightWithoutScaling, this.width);

    const panelHeight: number = +panelHeightWithoutScaling * scale;
    const panelWidth: number = +panelWidthWithoutscaling * scale;

    const data: PathData[] = [];

    // X axis
    data.push({ mode: 'M', data: [0, +this.startDrawPoint.y + base.y * scale] });
    data.push({ mode: 'l', data: [this.width + this.startDrawPoint.x, 0] });
    data.push({ mode: 'q', data: [-10, -1, -10, -5] });
    data.push({ mode: 'm', data: [0, 10] });
    data.push({ mode: 'q', data: [0, -4, 10, -5] });

    // Y axis
    data.push({ mode: 'M', data: [this.startDrawPoint.x + base.x * scale, this.height + this.startDrawPoint.y] });
    data.push({ mode: 'l', data: [0, -(this.height + this.startDrawPoint.y)] });
    data.push({ mode: 'q', data: [-1, 10, -5, 10] });
    data.push({ mode: 'm', data: [10, 0] });
    data.push({ mode: 'q', data: [-4, 0, -5, -10] });

    return data;
  }

  getSideSelectors(panel: PanelBase): PathData[][] {
    const scale =
      panel.width > panel.height
        ? this.getPanelScale(panel.width, this.height)
        : this.getPanelScale(panel.height, this.width);

    const panelHeight: number = +panel.height * scale;
    const panelWidth: number = +panel.width * scale;

    const data: PathData[][] = [];

    const adder = this.addSideSelectors(panelHeight, panelWidth);

    data.push(adder('tc'));
    data.push(adder('rc'));
    data.push(adder('bc'));
    data.push(adder('lc'));

    return data;
  }

  private addSideSelectors(scaledPanelHeight: number, scaledPanelWidth: number): (side: Position) => PathData[] {
    return (side: Position) => {
      const line: PathData[] = [];

      const sideSelectorOffset = 10;

      switch (side) {
        case 'tc':
          line.push({ mode: 'M', data: [this.startDrawPoint.x, this.startDrawPoint.y - sideSelectorOffset] });
          line.push({ mode: 'l', data: [+scaledPanelHeight, 0] });
          return line;

        case 'rc':
          line.push({
            mode: 'M',
            data: [this.startDrawPoint.x + scaledPanelHeight + sideSelectorOffset, this.startDrawPoint.y],
          });
          line.push({ mode: 'l', data: [0, +scaledPanelWidth] });
          return line;

        case 'bc':
          line.push({
            mode: 'M',
            data: [
              this.startDrawPoint.x + scaledPanelHeight,
              this.startDrawPoint.y + scaledPanelWidth + sideSelectorOffset,
            ],
          });
          line.push({ mode: 'l', data: [-scaledPanelHeight, 0] });
          return line;

        case 'lc':
          line.push({
            mode: 'M',
            data: [this.startDrawPoint.x - sideSelectorOffset, this.startDrawPoint.y + scaledPanelWidth],
          });
          line.push({ mode: 'l', data: [0, -scaledPanelWidth] });
          return line;
      }
    };
  }

  private addCustomHole(
    scale: number,
    panelHeight: number,
    panelWidth: number,
    side: Position,
    highlightedHole?: CuttingCustomHole[] | undefined
  ): (hole: CuttingCustomHole) => HolePathData {
    return (hole: CuttingCustomHole): HolePathData => {
      const circle: HolePathData = { class: '', data: [], hole: hole };

      switch (hole.type) {
        case PlateTypeEnum.Face:
          circle.class = 'c-hole-face';
          break;

        case PlateTypeEnum.Back:
          circle.class = 'c-hole-back';
          break;

        case PlateTypeEnum.Edge:
          circle.class = 'c-hole-edge';
          break;
      }
      if (highlightedHole && highlightedHole.some(h => _.isEqual(hole, h))) {
        circle.class += ' selected';
      }
      if (hole.depth === 0) {
        circle.class += ' through';
      }

      const scaledX = hole.x * scale;
      const scaledY = hole.y * scale;
      const scaledR = (hole.diameter / 2) * scale;
      const scaledDepth = hole.depth * scale;

      let holePos: Point = { x: 0, y: 0 };
      switch (side) {
        case 'tc':
          holePos =
            hole.basePoint === FastenerBasePointEnum.Start
              ? { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + scaledY }
              : { x: this.startDrawPoint.x + panelHeight - scaledX, y: this.startDrawPoint.y + scaledY };
          break;

        case 'rc':
          holePos =
            hole.basePoint === FastenerBasePointEnum.Start
              ? { x: this.startDrawPoint.x + panelHeight - scaledX, y: this.startDrawPoint.y + scaledY }
              : { x: this.startDrawPoint.x + panelHeight - scaledX, y: this.startDrawPoint.y + panelWidth - scaledY };
          break;

        case 'bc':
          holePos =
            hole.basePoint === FastenerBasePointEnum.Start
              ? { x: this.startDrawPoint.x + panelHeight - scaledX, y: this.startDrawPoint.y + panelWidth - scaledY }
              : { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + panelWidth - scaledY };
          break;

        case 'lc':
          holePos =
            hole.basePoint === FastenerBasePointEnum.Start
              ? { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + panelWidth - scaledY }
              : { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + scaledY };
          break;
      }

      const increasePx = 2.5;

      switch (hole.type) {
        case PlateTypeEnum.Face:
        case PlateTypeEnum.Back:
          circle.data.push({ mode: 'M', data: [holePos.x - scaledR - increasePx, holePos.y] });
          circle.data.push({
            mode: 'a',
            data: [scaledR + increasePx, scaledR + increasePx, 0, 1, 0, scaledR * 2 + increasePx * 2, 0],
          });
          circle.data.push({
            mode: 'a',
            data: [scaledR + increasePx, scaledR + increasePx, 0, 1, 0, -scaledR * 2 - increasePx * 2, 0],
          });
          break;

        case PlateTypeEnum.Edge:
          if (side === 'lc') {
            circle.data.push({ mode: 'M', data: [this.startDrawPoint.x, holePos.y - scaledR] });
            circle.data.push({ mode: 'l', data: [scaledDepth, 0] });
            circle.data.push({ mode: 'l', data: [0, hole.diameter * scale] });
            circle.data.push({ mode: 'l', data: [-scaledDepth, 0] });
            circle.data.push({ mode: 'l', data: [0, -hole.diameter * scale] });
          }
          if (side === 'rc') {
            circle.data.push({ mode: 'M', data: [panelHeight + this.startDrawPoint.x, holePos.y - scaledR] });
            circle.data.push({ mode: 'l', data: [-scaledDepth, 0] });
            circle.data.push({ mode: 'l', data: [0, hole.diameter * scale] });
            circle.data.push({ mode: 'l', data: [scaledDepth, 0] });
            circle.data.push({ mode: 'l', data: [0, -hole.diameter * scale] });
          }
          if (side === 'tc') {
            circle.data.push({ mode: 'M', data: [holePos.x - scaledR, this.startDrawPoint.y] });
            circle.data.push({ mode: 'l', data: [0, scaledDepth] });
            circle.data.push({ mode: 'l', data: [hole.diameter * scale, 0] });
            circle.data.push({ mode: 'l', data: [0, -scaledDepth] });
            circle.data.push({ mode: 'l', data: [-hole.diameter * scale, 0] });
          }
          if (side === 'bc') {
            circle.data.push({ mode: 'M', data: [holePos.x - scaledR, panelWidth + this.startDrawPoint.y] });
            circle.data.push({ mode: 'l', data: [0, -scaledDepth] });
            circle.data.push({ mode: 'l', data: [hole.diameter * scale, 0] });
            circle.data.push({ mode: 'l', data: [0, scaledDepth] });
            circle.data.push({ mode: 'l', data: [-hole.diameter * scale, 0] });
          }
          break;
      }

      return circle;
    };
  }

  private addCutout(
    scale: number,
    panelHeight: number,
    panelWidth: number
  ): (cutout: Cutout) => Promise<CutoutPathData | CutoutFragmentData> {
    return async (cutout: Cutout): Promise<CutoutPathData | CutoutFragmentData> => {
      const data: CutoutPathData | CutoutFragmentData = {
        base: {
          cx: 0,
          cy: 0,
          r: 0,
        },
        cutout: [],
      } as CutoutPathData | CutoutFragmentData;

      let cutoutWidth = cutout.width;
      let cutoutHeight = cutout.height;

      if (cutout.type === CutoutTypeEnum.Fragment) {
        cutoutWidth = cutout.height;
        cutoutHeight = cutout.width;
      }

      const scaledX = cutout.x * scale;
      const scaledY = cutout.y * scale;
      const scaledH = cutoutHeight * scale;
      const scaledW = cutoutWidth * scale;
      const scaledR = cutout.radius * scale;

      let cutoutPos: Point = { x: 0, y: 0 };
      switch (cutout.angle) {
        case CutoutPoint.TopLeft:
          data.base = {
            cx: this.startDrawPoint.x + scaledX,
            cy: this.startDrawPoint.y + scaledY,
            r: 3,
          };
          switch (cutout.basePoint) {
            case CutoutPoint.TopLeft:
              cutoutPos = { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + scaledY };
              break;
            case CutoutPoint.TopRight:
              cutoutPos = { x: this.startDrawPoint.x + scaledX - scaledH, y: this.startDrawPoint.y + scaledY };
              break;
            case CutoutPoint.Center:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX - scaledH / 2.0,
                y: this.startDrawPoint.y + scaledY - scaledW / 2.0,
              };
              break;
            case CutoutPoint.BottomRight:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX - scaledH,
                y: this.startDrawPoint.y + scaledY - scaledW,
              };
              break;
            case CutoutPoint.BottomLeft:
              cutoutPos = { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + scaledY - scaledW };
              break;
            case CutoutPoint.Custom:
              {
                cutoutPos = {
                  x: this.startDrawPoint.x + scaledX - cutout.fragmentParams.x * scale,
                  y: this.startDrawPoint.y + scaledY - (cutout.width - cutout.fragmentParams.y) * scale,
                };
              }
              break;
          }
          break;

        case CutoutPoint.TopRight:
          data.base = {
            cx: this.startDrawPoint.x + panelHeight - scaledX,
            cy: this.startDrawPoint.y + scaledY,
            r: 3,
          };
          switch (cutout.basePoint) {
            case CutoutPoint.TopLeft:
              cutoutPos = { x: this.startDrawPoint.x + panelHeight - scaledX, y: this.startDrawPoint.y + scaledY };
              break;
            case CutoutPoint.TopRight:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX - scaledH,
                y: this.startDrawPoint.y + scaledY,
              };
              break;
            case CutoutPoint.Center:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX - scaledH / 2.0,
                y: this.startDrawPoint.y + scaledY - scaledW / 2.0,
              };
              break;
            case CutoutPoint.BottomRight:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX - scaledH,
                y: this.startDrawPoint.y + scaledY - scaledW,
              };
              break;
            case CutoutPoint.BottomLeft:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX,
                y: this.startDrawPoint.y + scaledY - scaledW,
              };
              break;
            case CutoutPoint.Custom:
              {
                cutoutPos = {
                  x: this.startDrawPoint.x + panelHeight - scaledX - cutout.fragmentParams.x * scale,
                  y: this.startDrawPoint.y + scaledY - (cutout.width - cutout.fragmentParams.y) * scale,
                };
              }
              break;
          }
          break;

        case CutoutPoint.BottomRight:
          data.base = {
            cx: this.startDrawPoint.x + panelHeight - scaledX,
            cy: this.startDrawPoint.y + panelWidth - scaledY,
            r: 3,
          };
          switch (cutout.basePoint) {
            case CutoutPoint.TopLeft:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX,
                y: this.startDrawPoint.y + panelWidth - scaledY,
              };
              break;
            case CutoutPoint.TopRight:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX + scaledH,
                y: this.startDrawPoint.y + panelWidth - scaledY,
              };
              break;
            case CutoutPoint.Center:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX - scaledH / 2.0,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW / 2.0,
              };
              break;
            case CutoutPoint.BottomRight:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX - scaledH,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW,
              };
              break;
            case CutoutPoint.BottomLeft:
              cutoutPos = {
                x: this.startDrawPoint.x + panelHeight - scaledX,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW,
              };
              break;
            case CutoutPoint.Custom:
              {
                cutoutPos = {
                  x: this.startDrawPoint.x + panelHeight - scaledX - cutout.fragmentParams.x * scale,
                  y: this.startDrawPoint.y + panelWidth - scaledY - (cutout.width - cutout.fragmentParams.y) * scale,
                };
              }
              break;
          }
          break;

        case CutoutPoint.BottomLeft:
          data.base = {
            cx: this.startDrawPoint.x + scaledX,
            cy: this.startDrawPoint.y + panelWidth - scaledY,
            r: 3,
          };
          switch (cutout.basePoint) {
            case CutoutPoint.TopLeft:
              cutoutPos = { x: this.startDrawPoint.x + scaledX, y: this.startDrawPoint.y + panelWidth - scaledY };
              break;
            case CutoutPoint.TopRight:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX - scaledH,
                y: this.startDrawPoint.y + panelWidth - scaledY,
              };
              break;
            case CutoutPoint.Center:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX - scaledH / 2.0,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW / 2.0,
              };
              break;
            case CutoutPoint.BottomRight:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX - scaledH,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW,
              };
              break;
            case CutoutPoint.BottomLeft:
              cutoutPos = {
                x: this.startDrawPoint.x + scaledX,
                y: this.startDrawPoint.y + panelWidth - scaledY - scaledW,
              };
              break;
            case CutoutPoint.Custom:
              {
                cutoutPos = {
                  x: this.startDrawPoint.x + scaledX - cutout.fragmentParams.x * scale,
                  y: this.startDrawPoint.y + panelWidth - scaledY - (cutout.width - cutout.fragmentParams.y) * scale,
                };
              }
              break;
          }
          break;
      }

      switch (cutout.type) {
        case CutoutTypeEnum.Rectangle:
          {
            const cutoutPath: PathData[] = [];
            cutoutPath.push({ mode: 'M', data: [cutoutPos.x, cutoutPos.y] });
            cutoutPath.push({ mode: 'l', data: [scaledH, 0] });
            cutoutPath.push({ mode: 'l', data: [0, scaledW] });
            cutoutPath.push({ mode: 'l', data: [-scaledH, 0] });
            cutoutPath.push({ mode: 'Z', data: [] });

            data.cutout = cutoutPath;
          }
          break;

        case CutoutTypeEnum.RoundedRectangle:
          {
            const cutoutPath: PathData[] = [];
            cutoutPath.push({ mode: 'M', data: [cutoutPos.x + scaledR, cutoutPos.y] });
            cutoutPath.push({ mode: 'l', data: [scaledH - scaledR * 2, 0] });
            cutoutPath.push({ mode: 'q', data: [scaledR, 0, scaledR, scaledR] });
            cutoutPath.push({ mode: 'l', data: [0, scaledW - scaledR * 2] });
            cutoutPath.push({ mode: 'q', data: [0, scaledR, -scaledR, scaledR] });
            cutoutPath.push({ mode: 'l', data: [-scaledH + scaledR * 2, 0] });
            cutoutPath.push({ mode: 'q', data: [-scaledR, 0, -scaledR, -scaledR] });
            cutoutPath.push({ mode: 'l', data: [0, -scaledW + scaledR * 2] });
            cutoutPath.push({ mode: 'q', data: [0, -scaledR, scaledR, -scaledR] });
            cutoutPath.push({ mode: 'Z', data: [] });

            data.cutout = cutoutPath;
          }
          break;

        case CutoutTypeEnum.Ellipse:
          {
            const cutoutPath: PathData[] = [];
            cutoutPath.push({ mode: 'M', data: [cutoutPos.x, cutoutPos.y + scaledW / 2.0] });
            cutoutPath.push({
              mode: 'A',
              data: [scaledH / 2.0, scaledW / 2.0, 0, 0, 1, cutoutPos.x + scaledH, cutoutPos.y + scaledW / 2.0],
            });
            cutoutPath.push({
              mode: 'A',
              data: [scaledH / 2.0, scaledW / 2.0, 0, 0, 1, cutoutPos.x, cutoutPos.y + scaledW / 2.0],
            });

            data.cutout = cutoutPath;
          }
          break;
        case CutoutTypeEnum.Fragment:
          {
            // Пользователь ожидает направление вращения как в стандартной системе координат, когда y+ идет вверх, а здесь y+ идет вниз
            // => надо инвертировать направление вращения, чтобы положительный угол вращал фигуру против часовой стрелки
            const rotateCoefficient = -1;

            const fragmentData: CutoutFragmentData = data as CutoutFragmentData;
            fragmentData.cutout = await fetch(`/api-cutting/standard-cutouts/image/${cutout?.fragmentImageS3Key}`).then(
              res => res.text()
            );
            fragmentData.x = cutoutPos.x;
            fragmentData.y = cutoutPos.y;
            fragmentData.height = scaledW;
            fragmentData.width = scaledH;
            fragmentData.rotateDeg = cutout.rotateDeg * rotateCoefficient;
          }
          break;
      }

      return data;
    };
  }

  private _getFixedFastenerSets(
    noScaledPanelHeight: number,
    noScaledPanelWidth: number,
    highlightedSet?: CuttingFixedFastenersSet | undefined
  ): (sets: CuttingFixedFastenersSet[], pos: Position) => FastenersSetPathData[] {
    return (sets: CuttingFixedFastenersSet[], pos: Position): FastenersSetPathData[] => {
      const data: FastenersSetPathData[] = [];

      // crutch for no canc of calced
      const _sets = _.cloneDeep(sets);

      if (_sets.length > 0) {
        _sets.forEach(s => {
          const pathData: FastenersSetPathData = { class: '', holes: [], fastenersSet: s };

          const recalcer = FastenersHelper.recalcFastenersSetHole(pos, s.basePoint, s.plate);
          s.holes.forEach(h => {
            h = recalcer(h, { x: s.x, y: s.y, z: 0 });
          });

          if (this.isEqualFastenerSets(highlightedSet, s)) {
            pathData.class = 'selected';
          }

          const adder = this._getCusttomHoles(noScaledPanelHeight, noScaledPanelWidth);
          // up edge holes, for first drawing
          const holes = s.holes.sort((a, b) => (a.type === PlateTypeEnum.Edge ? -1 : 0));
          pathData.holes.push(...adder(s.holes, pos));
          data.push(pathData);
        });
      }

      return data;
    };
  }

  private isEqualFastenerSets(one: CuttingFixedFastenersSet, two: CuttingFixedFastenersSet): boolean {
    const comparedSet = _.cloneDeep(one);
    const comparedHSet = _.cloneDeep(two);
    if (comparedSet) {
      comparedSet.holes = [];
    }
    if (comparedHSet) {
      comparedHSet.holes = [];
    }
    return _.isEqual(comparedSet, comparedHSet);
  }

  private addGroove(
    scale: number,
    panelHeight: number,
    panelWidth: number
  ): (groove: CuttingPanelSlot, position: Position) => RectangleData {
    return (groove: CuttingPanelSlot, position: Position): RectangleData => {
      const chamferGrooveDrawWidth = 5;

      const rect: RectangleData = { x: 0, y: 0, height: 0, width: 0, class: '', id: position + '-groove' };

      if (!groove) {
        return rect;
      }

      let lineClass = '';
      switch (groove.type) {
        case GrooveTypeEnum.Rabbet:
          lineClass = 'quater';
          break;

        case GrooveTypeEnum.InPlate:
          lineClass = 'in-plate';
          break;

        case GrooveTypeEnum.InSide:
          lineClass = 'in-side';
          break;

        case GrooveTypeEnum.Chamfer:
          lineClass = 'chamfer';
          break;

        case GrooveTypeEnum.Chamfer2:
          lineClass = 'chamfer2';
          break;

        case GrooveTypeEnum.Fillet:
          lineClass = 'fillet';
          break;
      }

      if (groove.plate === PlateTypeEnum.Back) {
        lineClass += ' back-plate';
      }

      rect.class = lineClass;

      let grooveWidth = 0;
      switch (groove.type) {
        case GrooveTypeEnum.Chamfer:
          grooveWidth = chamferGrooveDrawWidth;
          break;

        case GrooveTypeEnum.Chamfer2:
          grooveWidth = +groove.b * scale;
          break;

        case GrooveTypeEnum.Fillet:
          grooveWidth = +groove.r * scale;
          break;

        case GrooveTypeEnum.InSide:
          grooveWidth = +groove.g * scale;
          break;

        default:
          grooveWidth = +groove.h * scale;
          break;
      }
      // up slot size
      grooveWidth = grooveWidth < 4 ? 4 : grooveWidth;

      const grooveShift = +groove.dx * scale;

      switch (position) {
        case 'tc':
          {
            rect.x = this.startDrawPoint.x;
            rect.y =
              groove.type === GrooveTypeEnum.InPlate ? grooveShift + this.startDrawPoint.y : this.startDrawPoint.y;
            rect.width = panelHeight;
            rect.height = grooveWidth;
          }
          break;

        case 'bc':
          {
            rect.x = this.startDrawPoint.x;
            rect.y =
              groove.type === GrooveTypeEnum.InPlate
                ? this.startDrawPoint.y + panelWidth - grooveShift - grooveWidth
                : this.startDrawPoint.y + panelWidth - grooveWidth;
            rect.width = panelHeight;
            rect.height = grooveWidth;
          }
          break;

        case 'lc':
          {
            rect.x =
              groove.type === GrooveTypeEnum.InPlate ? this.startDrawPoint.x + grooveShift : this.startDrawPoint.x;
            rect.y = this.startDrawPoint.y;
            rect.width = grooveWidth;
            rect.height = panelWidth;
          }
          break;

        case 'rc':
          {
            rect.x =
              groove.type === GrooveTypeEnum.InPlate
                ? this.startDrawPoint.x + panelHeight - grooveWidth - grooveShift
                : this.startDrawPoint.x + panelHeight - grooveWidth;
            rect.y = this.startDrawPoint.y;
            rect.width = grooveWidth;
            rect.height = panelWidth;
          }
          break;
      }

      return rect;
    };
  }

  private addAngleText(
    data: TextData[],
    scale: number,
    panelHeight: number,
    panelWidth: number
  ): (angle: OutlineAngle, anglePos: AnglePosition) => void {
    return (angle: OutlineAngle, anglePos: AnglePosition) => {
      let singleTextVal = 0;
      let singleTextDisplayVal = '';
      let radius = 1;

      let cutVal: Point = { x: 0, y: 0 };
      let cutDisplayVal: Point = { x: 0, y: 0 };

      const panelHeightScaled: number = +panelHeight * scale;
      const panelWidthScaled: number = +panelWidth * scale;

      switch (angle.mode) {
        case AngleMode.Rounded:
          {
            const rounding: number = angle.rounding * scale;
            singleTextVal = rounding;
            singleTextDisplayVal = `R = ${angle.rounding}mm`;
          }
          break;

        case AngleMode.RoundedInside:
          {
            const rounding: number = angle.roundingInside * scale;
            singleTextVal = rounding;
            singleTextDisplayVal = `R = ${angle.roundingInside}mm`;
          }
          break;

        case AngleMode.Cutted:
          {
            cutVal = { x: angle.cutX * scale, y: angle.cutY * scale };
            cutDisplayVal = { x: angle.cutX, y: angle.cutY };
          }
          break;

        case AngleMode.CuttedInside:
          {
            cutVal = { x: angle.cutInsideX * scale, y: angle.cutInsideY * scale };
            cutDisplayVal = { x: angle.cutInsideX, y: angle.cutInsideY };
            radius = angle.cutInsideR * scale;
          }
          break;

        case AngleMode.Chamfer:
          {
            singleTextVal = angle.chamferLength * scale;
            singleTextDisplayVal = angle.chamferLength + 'mm';
          }
          break;
      }

      this.addSingleAngleText(
        data,
        singleTextVal,
        anglePos as Position,
        panelHeightScaled,
        panelWidthScaled,
        singleTextDisplayVal
      );
      // this.addTextCut(
      //   data,
      //   cutVal,
      //   (anglePos + 'x') as Position,
      //   (anglePos + 'y') as Position,
      //   panelHeightScaled,
      //   panelWidthScaled,
      //   cutDisplayVal,
      //   radius
      // );
    };
  }

  private addSingleAngleText(
    data: TextData[],
    value: number,
    positionRounding: Position,
    panelHeight: number,
    panelWidth: number,
    displayVal: string
  ): void {
    let radiusName = '';
    const textLengthPx = 60;

    if (+value > 0) {
      radiusName = displayVal;
    }

    let xRound = 0;
    let yRound = 0;

    switch (positionRounding) {
      case 'tl':
        xRound = +this.startDrawPoint.x + +value;
        yRound = +this.startDrawPoint.y + +value + this.textOffset + this.textSize;
        break;
      case 'tr':
        xRound = +this.startDrawPoint.x + +panelHeight - +value - +textLengthPx;
        yRound = +this.startDrawPoint.y + +value + this.textOffset + this.textSize;

        break;
      case 'br':
        xRound = +this.startDrawPoint.x + +panelHeight - +value - +textLengthPx;
        yRound = +this.startDrawPoint.y + +panelWidth - +value - this.textOffset;
        break;
      case 'bl':
        xRound = +this.startDrawPoint.x + +value;
        yRound = +this.startDrawPoint.y + +panelWidth - +value - this.textOffset;
        break;
    }

    data.push({
      text: radiusName,
      position: positionRounding,
      x: xRound,
      y: yRound,
    });
  }

  private _getCusttomHoles(
    noScaledPanelHeight: number,
    noScaledPanelWidth: number,
    highlightedHole?: CuttingCustomHole[] | undefined
  ): (customHoles: CuttingCustomHole[], pos: Position) => HolePathData[] {
    return (customHoles: CuttingCustomHole[], pos: Position): HolePathData[] => {
      const scale =
        noScaledPanelWidth > noScaledPanelHeight
          ? this.getPanelScale(noScaledPanelWidth, this.height)
          : this.getPanelScale(noScaledPanelHeight, this.width);

      const panelHeight: number = +noScaledPanelHeight * scale;
      const panelWidth: number = +noScaledPanelWidth * scale;

      const data: HolePathData[] = [];

      const adder = this.addCustomHole(scale, panelHeight, panelWidth, pos, highlightedHole);
      customHoles.forEach(h => {
        data.push(adder(h));
      });

      return data;
    };
  }

  private _getCutouts(
    noScaledPanelHeight: number,
    noScaledPanelWidth: number
  ): (cutouts: Cutout[]) => Promise<Array<CutoutPathData | CutoutFragmentData>> {
    return async (cutouts: Cutout[]): Promise<Array<CutoutPathData | CutoutFragmentData>> => {
      const scale =
        noScaledPanelWidth > noScaledPanelHeight
          ? this.getPanelScale(noScaledPanelWidth, this.height)
          : this.getPanelScale(noScaledPanelHeight, this.width);

      const panelHeight: number = +noScaledPanelHeight * scale;
      const panelWidth: number = +noScaledPanelWidth * scale;

      const adder = this.addCutout(scale, panelHeight, panelWidth);
      const data = await Promise.all(
        cutouts.map(async h => {
          return await adder(h);
        })
      );

      return data;
    };
  }

  private getDefs(): Element {
    const defs = document.createElementNS(this.svgNS, 'defs');
    const highLightFilter = document.createElementNS(this.svgNS, 'filter');
    const highLight = document.createElementNS(this.svgNS, 'feDropShadow');
    highLightFilter.setAttribute('id', 'high-light');
    highLightFilter.setAttribute('x', '-500%');
    highLightFilter.setAttribute('y', '-500%');
    highLightFilter.setAttribute('height', '1000%');
    highLightFilter.setAttribute('width', '1000%');

    highLight.setAttribute('dx', '0');
    highLight.setAttribute('dy', '0');
    highLight.setAttribute('stdDeviation', '2');
    highLight.setAttribute('in', 'SourceGraphic');
    highLight.setAttribute('flood-color', 'greenyellow');
    highLight.setAttribute('result', 'dropShadow2');

    highLightFilter.appendChild(highLight);
    defs.appendChild(highLightFilter);

    return defs;
  }

  getPanelScale(val: number, container: number): number {
    return (container - this.startDrawPoint.x * 2) / val;
  }

  interpolateLinear(data: PathData[]): string {
    return data
      .map(d => {
        return d.mode + d.data.join(' ');
      })
      .join(' ');
  }

  private deg2rad = (deg: number) => deg * (Math.PI / 180);
  private rad2deg = (rad: number) => (rad * 180) / Math.PI;

  private mirrorCoord = (center: number, coord: number) => center - (coord - center);

  // private anglePoint2SVGPoint(point: Point, topLeftPoint: Point, bottomRight: Point, angle: AnglePosition) {
  //   switch (angle) {
  //     case 'tl':
  //       return { x: topLeftPoint.x + point.x, y: topLeftPoint.y - point.y };
  //     case 'tr':
  //       return { x: bottomRight.x + point.x, y: topLeftPoint.y - point.y };
  //     case 'br':
  //       return { x: bottomRight.x + point.x, y: bottomRight.y - point.y };
  //     case 'bl':
  //       return { x: topLeftPoint.x + point.x, y: topLeftPoint.y + point.y };

  //     default:
  //       break;
  //   }
  // }

  // private sumPoints({ x: x1, y: y1 }: Point, { x: x2, y: y2 }: Point) {
  //   return { x: x1 + x2, y: y1 + y2 };
  // }

  // private findPointsOfTangency(
  //   vertex: [number, number],
  //   angle: number,
  //   radius: number
  // ): [[number, number], [number, number]] {
  //   // вычисляем координаты центра окружности
  //   const centerX = vertex[0] - radius * Math.cos(angle / 2);
  //   const centerY = vertex[1] + radius * Math.sin(angle / 2);

  //   // вычисляем коэффициенты уравнения касательной к окружности, проходящей через вершину угла
  //   const slope = -Math.tan(angle / 2);
  //   const yIntercept = vertex[1] - slope * vertex[0];

  //   // вычисляем координаты точек пересечения касательной и прямой, описывающей стороны угла
  //   const a = slope ** 2 + 1;
  //   const b = 2 * (slope * yIntercept - slope * centerY - centerX);
  //   const c = centerY ** 2 + centerX ** 2 + yIntercept ** 2 - 2 * yIntercept * centerY - radius ** 2;
  //   const discriminant = b ** 2 - 4 * a * c;
  //   const x1 = (-b + Math.sqrt(discriminant)) / (2 * a);
  //   const y1 = slope * x1 + yIntercept;
  //   const x2 = (-b - Math.sqrt(discriminant)) / (2 * a);
  //   const y2 = slope * x2 + yIntercept;

  //   return [
  //     [x1, y1],
  //     [x2, y2],
  //   ];
  // }

  private getCircleRemotenessFromCornerTop(angle: number, r: number) {
    // Расстояние до центра окружности
    const distanceToCircleCenter = r / Math.sin(this.deg2rad(angle) / 2);
    // Расстояние от вершины угла до пересечения окружности и касательной
    const distanceToCircleIntersection = Math.sqrt(distanceToCircleCenter ** 2 - r ** 2);

    return distanceToCircleIntersection;
  }

  /**
   * Метод, вычисляющий точку на отрезке AB от точки A на заданное расстояние
   * @param distance
   * @param startPoint
   * @param endpointB
   */
  pointOnSegment(distance: number, startPoint: Point, endpointB: Point): Point | null {
    const dx = endpointB.x - startPoint.x;
    const dy = endpointB.y - startPoint.y;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    const t = distance / segmentLength;

    const precision = 0.0001;

    // Если расстояние больше длины отрезка AB, то возвращаем null
    if (t - 1 >= precision) {
      return null;
    }

    // Вычисляем координаты точки на отрезке AB
    const px = startPoint.x + dx * t;
    const py = startPoint.y + dy * t;

    return { x: px, y: py };
  }

  /**
   *
   *Поиск пересечения двух функций y(x)=tan(angle)*(x-xOffset)+yOffset. (y=xk+b)
   * @param xAngle1 Угол отклонения от оси X в градусах для первой функции. Положительное число — против часовой стрелки, отрицательное — по часовой стрелке
   * @param angle1
   * @param xOffset1 Смещение по оси X для первой функции
   * @param yOffset1 Смещение по оси Y для первой функции
   * @param xAngle2 Угол отклонения от оси X в градусах для второй функции. Положительное число — против часовой стрелки, отрицательное — по часовой стрелке
   * @param angle2
   * @param xOffset2 Смещение по оси X для второй функции
   * @param yOffset2 Смещение по оси Y для второй функции
   * @returns Координаты точки пересечения функций, Если пересечений нет, возвращается NaN
   */
  private findIntersectionPoint(
    angle1: number,
    xOffset1: number,
    yOffset1: number,
    angle2: number,
    xOffset2: number,
    yOffset2: number
  ): { point: Point; zeroPoint1: Point; zeroPoint2: Point; angle: number } {
    // Расчет угловых коэффициентов
    const m1 = Math.tan(this.deg2rad(angle1));
    const m2 = Math.tan(this.deg2rad(angle2));

    // Если угол наклона функций одинаков, то они не пересекаются
    if (m1 === m2) {
      return null;
    }

    // Расчет координат точки пересечения
    const x = (yOffset2 - yOffset1 + m1 * xOffset1 - m2 * xOffset2) / (m1 - m2);
    const y = m1 * (x - xOffset1) + yOffset1;

    // Угол образованный прямыми
    let angle = 0;
    if (1 + m1 * m2 !== 0) {
      angle = Math.abs(this.rad2deg(Math.atan((m1 - m2) / (1 + m1 * m2))));
    } else {
      angle = 90;
    }

    // Координаты начала отрезка от оси до пересечения прямых
    const zeroPoint1 = { x: xOffset1, y: yOffset1 };
    const zeroPoint2 = { x: xOffset2, y: yOffset2 };

    return { point: { x, y }, zeroPoint1, zeroPoint2, angle };
  }
}
